<template>
    <form :id="id" @submit.prevent="handleSubmit" class="form-horizontal">
        <fieldset :disabled="disabled">
            <x-form-group label="ID" v-if="displayId">
                <input
                    type="text"
                    class="form-control"
                    v-model="fields.id"
                    disabled
                />
            </x-form-group>

            <x-form-group label="Kode Jemaat" v-if="displayCode">
                <input
                    type="text"
                    class="form-control"
                    v-model="fields.code"
                    disabled
                />
                <img
                    :src="fields.qr_url"
                    class="img-responsive"
                    style="max-height: 100px"
                />
            </x-form-group>

            <x-form-group
                label="Nama Lengkap"
                :error="getError('name')"
                :is-required="true"
            >
                <template v-if="enableNameCheck">
                    <input
                        type="text"
                        class="form-control"
                        v-model="fields.name"
                        @keyup="handleNameChange"
                        :disabled="isLoading"
                        required
                    />
                    <span class="help-block" v-if="isLoading">
                        <x-loading :wrap="false" /> Memeriksa nama yang kembar
                    </span>
                    <div class="text-danger" v-else-if="duplicateName">
                        <i class="fa fa-exclamation-triangle" /> Nama sudah
                        digunakan
                    </div>
                    <div class="text-success" v-else-if="Boolean(fields.name)">
                        <i class="fa fa-check" /> Nama belum pernah digunakan
                    </div>
                </template>
                <input
                    v-else
                    type="text"
                    class="form-control"
                    v-model="fields.name"
                />
            </x-form-group>

            <x-form-group
                label="Jenis Kelamin"
                :error="getError('gender')"
                :is-required="true"
            >
                <x-input-gender v-model="fields.gender" :required="true" />
            </x-form-group>

            <x-form-group
                label="Tempat Lahir"
                :error="getError('birthplace')"
                :is-required="false"
            >
                <input
                    type="text"
                    class="form-control"
                    v-model="fields.birthplace"
                />
            </x-form-group>

            <x-form-group
                label="Tanggal Lahir"
                :error="getError('birthdate')"
                :is-required="true"
            >
                <input
                    type="date"
                    class="form-control"
                    v-model="fields.birthdate"
                />
            </x-form-group>

            <x-form-group
                label="Alamat"
                :error="getError('address')"
                :is-required="false"
            >
                <textarea class="form-control" v-model="fields.address" />
            </x-form-group>

            <x-form-group
                label="No. Telpon"
                :error="getError('phone')"
                :is-required="true"
            >
                <input
                    type="text"
                    class="form-control"
                    v-model="fields.phone"
                    required
                />
            </x-form-group>

            <x-form-group
                label="Email"
                :error="getError('email')"
                :is-required="false"
            >
                <input
                    type="email"
                    class="form-control"
                    v-model="fields.email"
                />
            </x-form-group>

            <x-form-group
                label="Status"
                :error="getError('status')"
                :is-required="true"
            >
                <x-member-status-dropdown v-model="fields.status" />
            </x-form-group>

            <x-form-group
                label="Status Pernikahan"
                :error="getError('marital_status')"
                :is-required="true"
            >
                <select
                    v-model="fields.marital_status"
                    class="form-control"
                    required
                >
                    <option
                        v-for="(status, index) in availableMaritalStatuses"
                        :value="status"
                        :key="index"
                    >
                        {{ status }}
                    </option>
                </select>
            </x-form-group>

            <x-form-group
                label="Tanggal Bergabung"
                :error="getError('join_date')"
                :is-required="false"
                help-text="Tanggal berkunjung saat ibadah"
                v-if="displayJoinDate"
            >
                <input
                    type="date"
                    class="form-control"
                    v-model="fields.join_date"
                />
            </x-form-group>

            <x-form-group
                label="Informasi Tambahan"
                v-if="fields.status === 'Pengunjung' && hasOtherFields"
            >

                <table class="table table-striped table-bordered">
                    <thead>
                        <tr>
                            <th>Key</th>
                            <th>Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in fields.other_fields" :key="key">
                            <td><small>{{ key }}</small></td>
                            <td><small>{{ value ?? "-" }}</small></td>
                        </tr>
                    </tbody>
                </table>
            </x-form-group>
        </fieldset>
    </form>
</template>

<script>
import XAjaxDropdown from "../../components/XAjaxDropdown.vue";
import XCheckboxWrapper from "../../components/XCheckboxWrapper.vue";
import XFilterBox from "../../components/XFilterBox.vue";
import XFormGroup from "../../components/XFormGroup.vue";
import XHelpWarning from "../../components/XHelpWarning.vue";
import XInputGender from "../../components/XInputGender.vue";
import XInputImage from "../../components/XInputImage.vue";
import XInputMember from "../../components/XInputMember.vue";
import XLoading from "../../components/XLoading.vue";
import XMemberStatusDropdown from "../../components/XMemberStatusDropdown.vue";
import {
    availableMemberMaritalStatuses,
    handleGeneralXhrError,
} from "../../helpers";
import FormDataMixin from "../../mixins/FormDataMixin";
import FormErrorMixin from "../../mixins/FormErrorMixin";
import PropertySyncMixin from "../../mixins/PropertySyncMixin";

export default {
    components: {
        XFormGroup,
        XFilterBox,
        XCheckboxWrapper,
        XAjaxDropdown,
        XLoading,
        XMemberStatusDropdown,
        XInputImage,
        XInputGender,
        XInputMember,
        XHelpWarning,
    },
    computed: {
        availableMaritalStatuses() {
            return availableMemberMaritalStatuses();
        },
        hasOtherFields() {
            return this.fields.other_fields && Object.keys(this.fields.other_fields).length > 0;
        },
    },
    created() {
        const genderMap = {
            male: "Laki-Laki",
            female: "Perempuan",
            unknown: "Tidak Diketahui",
        }
        this.fields = {
            ...this.fields,
            ...this.initialFields,
        };

        if (this.fields.gender && genderMap[this.fields.gender]) {
            this.fields.gender = genderMap[this.fields.gender];
        }
        this.initialStatus = this.fields.status;
    },
    data() {
        return {
            duplicateName: false,
            fields: {
                id: null,
                name: null,
                gender: "Laki-Laki",
                birthplace: null,
                birthdate: null,
                address: null,
                phone: null,
                email: null,
                status: "Pengunjung",
                marital_status: _.get(this.availableMaritalStatuses, "0"),
                join_date: null,
                other_fields: {},
            },
            initialStatus: null,
            isLoading: false,
        };
    },
    methods: {
        checkName(name) {
            this.isLoading = true;
            const request = axios
                .get(`/api/visitors/name-check`, {
                    params: { name },
                })
                .then((response) => {
                    this.duplicateName = Boolean(response.data.duplicate);
                });
            return handleGeneralXhrError(request, (errors) => {
                this.duplicateName = false;
            }).finally(() => {
                this.isLoading = false;
            });
        },
        handleNameChange: _.debounce(function (event) {
            if (!Boolean(event.target.value)) {
                return;
            }
            return this.checkName(event.target.value);
        }, 750),
        handleSubmit() {
            const submit = () => {
                const { guardians, ...fields } = this.fields;
                let formData = this.buildFormData(fields);
                this.$emit("submit", formData);
            };

            submit();
        },
    },
    mounted() {
        if (this.enableNameCheck) {
            this.checkName(this.fields.name);
        }
    },
    mixins: [FormDataMixin, FormErrorMixin, PropertySyncMixin],
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        displayCode: {
            type: Boolean,
            required: false,
            default: false,
        },
        displayId: {
            type: Boolean,
            required: false,
            default: false,
        },
        displayJoinDate: {
            type: Boolean,
            required: false,
            default: true,
        },
        enableNameCheck: {
            type: Boolean,
            required: false,
            default: false,
        },
        errors: {
            type: Object,
            required: false,
            default: null,
        },
        id: {
            type: String,
            required: false,
            default: null,
        },
        initialFields: {
            type: Object,
            required: false,
            default: () => ({}),
        },
    },
};
</script>
